import { TextField } from "@mui/material";
import { useGlobalContext } from "@root/context";
import { AutocompleteSettings } from "@styleguide";
import { useMemo } from "react";
import { type Control, Controller, useWatch } from "react-hook-form";
import type { IModalActionForm, IModalActionResolver } from "../logics/types";

export interface IModalActionFormActionTypeProps {
  control: Control<IModalActionForm, void, IModalActionResolver>;
  disabledActionTypes?: string[];
  disableActionTypeField?: boolean;
}

export const ModalActionFormActionType = ({
  control,
  disabledActionTypes,
  disableActionTypeField,
}: IModalActionFormActionTypeProps) => {
  const { settingsV2 } = useGlobalContext();
  const actionType = useWatch({
    control,
    name: "type",
  });

  const options = useMemo(
    () =>
      settingsV2.statusTodoType?.values.filter((option) => {
        if (disabledActionTypes?.includes(option.value)) {
          return false;
        }
        return !option.isAutoTask && option.active !== false;
      }) ?? [],
    [settingsV2.statusTodoType, disabledActionTypes]
  );

  const optionsWithCurrent = useMemo(() => {
    if (actionType?.active === false) {
      return [{ ...actionType, label: `${actionType.label} (désactivé)` }, ...options];
    }
    if (actionType?.isAutoTask === true) {
      return [{ ...actionType, label: `${actionType.label} (automatique)` }, ...options];
    }
    return options;
  }, [actionType, options]);

  return (
    <Controller
      control={control}
      name="type"
      render={({ field: { value, onChange, onBlur, name, ref }, fieldState: { error } }) => {
        return (
          <AutocompleteSettings
            value={value}
            onChange={(_, value) => onChange(value)}
            onBlur={onBlur}
            options={optionsWithCurrent}
            size="small"
            isOptionEqualToValue={(option, value) => option.value === value.value}
            disabled={disableActionTypeField}
            renderInput={(params) => (
              <TextField
                {...params}
                name={name}
                label="Type d'action *"
                inputRef={ref}
                error={Boolean(error)}
                helperText={error?.message}
                disabled={disableActionTypeField}
              />
            )}
          />
        );
      }}
    />
  );
};
